import { useCallback, useEffect, useRef, useState } from "react";
import useNotiStack from "hooks/useNotistack";
import { computeInitialBounds, normalizeRotation } from "./overlayGeometry";

export const MAX_OVERLAY_IMAGES = 15;
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const SIZE_WARN_BYTES = 25 * 1024 * 1024;
const DEFAULT_OPACITY = 0.7;

const loadImageSize = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = url;
  });

const useImageOverlays = (mapRef) => {
  const [overlays, setOverlays] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const overlaysRef = useRef(overlays);
  overlaysRef.current = overlays;
  const idCounterRef = useRef(0);
  const generationRef = useRef(0);
  const overlayCountRef = useRef(0);
  const { toastError, toastWarning } = useNotiStack();

  useEffect(
    () => () => {
      overlaysRef.current.forEach((overlay) =>
        URL.revokeObjectURL(overlay.url)
      );
    },
    []
  );

  const addImages = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      const map = mapRef.current;
      if (!files.length || !map) return;

      const accepted = files.filter((file) => {
        if (ACCEPTED_TYPES.includes(file.type)) return true;
        toastError(`${file.name} is not a supported image type.`);
        return false;
      });

      const room = MAX_OVERLAY_IMAGES - overlayCountRef.current;
      if (accepted.length > room) {
        toastWarning(
          `You cannot add more than ${MAX_OVERLAY_IMAGES} overlay images.`
        );
        accepted.length = Math.max(room, 0);
      }

      const viewport = map.getBounds();
      const viewportBounds = [
        [viewport.getSouth(), viewport.getWest()],
        [viewport.getNorth(), viewport.getEast()],
      ];

      const generation = generationRef.current;

      for (const file of accepted) {
        if (file.size > SIZE_WARN_BYTES) {
          toastWarning(`${file.name} is larger than 25 MB and may slow down the map.`);
        }
        const url = URL.createObjectURL(file);
        try {
          const { width, height } = await loadImageSize(url);
          if (generationRef.current !== generation) {
            URL.revokeObjectURL(url);
            return;
          }
          if (overlayCountRef.current >= MAX_OVERLAY_IMAGES) {
            URL.revokeObjectURL(url);
            toastWarning(
              `You cannot add more than ${MAX_OVERLAY_IMAGES} overlay images.`
            );
            return;
          }
          overlayCountRef.current += 1;
          idCounterRef.current += 1;
          const overlay = {
            id: `imageOverlay_${idCounterRef.current}`,
            name: file.name,
            url,
            bounds: computeInitialBounds(viewportBounds, width, height),
            rotation: 0,
            opacity: DEFAULT_OPACITY,
            visible: true,
          };
          setOverlays((prev) => [...prev, overlay]);
          setEditingId(overlay.id);
        } catch {
          URL.revokeObjectURL(url);
          toastError(`Could not load ${file.name}.`);
        }
      }
    },
    [mapRef, toastError, toastWarning]
  );

  const removeOverlay = useCallback((id) => {
    const target = overlaysRef.current.find((overlay) => overlay.id === id);
    if (target) {
      URL.revokeObjectURL(target.url);
      overlayCountRef.current -= 1;
    }
    setOverlays((prev) => prev.filter((overlay) => overlay.id !== id));
    setEditingId((current) => (current === id ? null : current));
  }, []);

  const toggleVisible = useCallback((id) => {
    const target = overlaysRef.current.find((overlay) => overlay.id === id);
    const willHide = Boolean(target?.visible);
    setOverlays((prev) =>
      prev.map((overlay) =>
        overlay.id === id ? { ...overlay, visible: !overlay.visible } : overlay
      )
    );
    setEditingId((current) => (current === id && willHide ? null : current));
  }, []);

  const setOverlayBounds = useCallback((id, bounds) => {
    setOverlays((prev) =>
      prev.map((overlay) =>
        overlay.id === id ? { ...overlay, bounds } : overlay
      )
    );
  }, []);

  const setOverlayRotation = useCallback((id, rotation) => {
    setOverlays((prev) =>
      prev.map((overlay) =>
        overlay.id === id
          ? { ...overlay, rotation: normalizeRotation(rotation) }
          : overlay
      )
    );
  }, []);

  const setOverlayOpacity = useCallback((id, opacity) => {
    setOverlays((prev) =>
      prev.map((overlay) =>
        overlay.id === id ? { ...overlay, opacity } : overlay
      )
    );
  }, []);

  const startEdit = useCallback((id) => {
    setOverlays((prev) =>
      prev.map((overlay) =>
        overlay.id === id ? { ...overlay, visible: true } : overlay
      )
    );
    setEditingId(id);
  }, []);

  const stopEdit = useCallback(() => {
    setEditingId(null);
  }, []);

  const clearAll = useCallback(() => {
    generationRef.current += 1;
    overlayCountRef.current = 0;
    overlaysRef.current.forEach((overlay) => URL.revokeObjectURL(overlay.url));
    setOverlays([]);
    setEditingId(null);
  }, []);

  return {
    overlays,
    editingId,
    addImages,
    removeOverlay,
    toggleVisible,
    setOverlayBounds,
    setOverlayRotation,
    setOverlayOpacity,
    startEdit,
    stopEdit,
    clearAll,
  };
};

export default useImageOverlays;
